import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { PacingProgressBar } from './pacing-progress-bar';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';
import { getDateRangeState } from './metric-date-utils';

type SpendProgressMetricProps = {
  dateRange: ISODateRange;
};

export function SpendProgressMetric({ dateRange }: SpendProgressMetricProps) {
  const { lineItem } = useLineItemContext();
  const state = getDateRangeState(dateRange);

  if (state === 'expired') return null;

  return <SpendProgressMetricInner lineItemId={lineItem.id} dateRange={dateRange} />;
}

function SpendProgressMetricInner({
  lineItemId,
  dateRange
}: {
  lineItemId: string;
  dateRange: ISODateRange;
}) {
  const result = useCubeQuery({
    measures: [
      'line_item_metrics_daily.spend_pacing',
      'line_item_metrics_daily.actual_spend',
      'line_item_metrics_daily.planned_spend',
      'line_item_metrics_daily.remaining_spend'
    ],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItemId]
      }
    ]
  });

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  if (!result.rows[0] || !result.formattedRows[0]) {
    return <PacingProgressBar pacing="$0.00" planned="$0.00" remaining="$0.00" pacingRaw={0} />;
  }

  const { 'line_item_metrics_daily.spend_pacing': pacingRaw } = result.rows[0];
  const {
    'line_item_metrics_daily.spend_pacing': pacing,
    'line_item_metrics_daily.planned_spend': planned,
    'line_item_metrics_daily.remaining_spend': remaining
  } = result.formattedRows[0];

  return (
    <PacingProgressBar
      pacing={pacing}
      planned={planned}
      remaining={remaining}
      pacingRaw={pacingRaw}
    />
  );
}
